import React, { useState, useEffect } from 'react';
import { apiClient } from '../../services/api';
import { Customer } from '../../types';
import { SurveySummary } from '../../types/survey';
import { useLanguage } from '../../contexts/LanguageContext';

interface CustomerWithSurveys extends Customer {
  surveys?: SurveySummary[];
  loadingSurveys?: boolean;
}

const CustomerManagement: React.FC = () => {
  const { t } = useLanguage();
  const [customers, setCustomers] = useState<CustomerWithSurveys[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  // Create customer states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [creating, setCreating] = useState(false);

  // Edit customer states
  const [editingCustomer, setEditingCustomer] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editProjectName, setEditProjectName] = useState('');
  const [editEmail, setEditEmail] = useState('');

  // Link survey states
  const [linkingCustomer, setLinkingCustomer] = useState<string | null>(null);
  const [availableSurveys, setAvailableSurveys] = useState<SurveySummary[]>([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Expanded customers (to show surveys)
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadCustomers();
  }, [showArchived]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const data = showArchived
        ? await apiClient.getArchivedCustomers()
        : await apiClient.getCustomers();
      setCustomers(data);
    } catch (err) {
      console.error('Failed to load customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerSurveys = async (customerId: string) => {
    setCustomers(prev => prev.map(c =>
      c.customerId === customerId ? { ...c, loadingSurveys: true } : c
    ));
    try {
      const surveys = await apiClient.getCustomerSurveys(customerId);
      setCustomers(prev => prev.map(c =>
        c.customerId === customerId ? { ...c, surveys, loadingSurveys: false } : c
      ));
    } catch (err) {
      console.error('Failed to load customer surveys:', err);
      setCustomers(prev => prev.map(c =>
        c.customerId === customerId ? { ...c, surveys: [], loadingSurveys: false } : c
      ));
    }
  };

  const toggleExpand = (customerId: string) => {
    const newExpanded = new Set(expandedCustomers);
    if (newExpanded.has(customerId)) {
      newExpanded.delete(customerId);
    } else {
      newExpanded.add(customerId);
      // Load surveys if not loaded yet
      const customer = customers.find(c => c.customerId === customerId);
      if (customer && !customer.surveys) {
        loadCustomerSurveys(customerId);
      }
    }
    setExpandedCustomers(newExpanded);
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerName.trim()) return;
    try {
      setCreating(true);
      await apiClient.createCustomer(newCustomerName.trim(), newEmail.trim() || undefined, newProjectName.trim() || undefined);
      setNewCustomerName('');
      setNewProjectName('');
      setNewEmail('');
      setShowCreateForm(false);
      loadCustomers();
    } catch (err) {
      console.error('Failed to create customer:', err);
      alert(t('customerManagement', 'createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const handleEditCustomer = async (customerId: string) => {
    try {
      await apiClient.updateCustomer(customerId, {
        customerName: editName.trim(),
        email: editEmail.trim(),
        projectName: editProjectName.trim()
      });
      setEditingCustomer(null);
      loadCustomers();
    } catch (err) {
      console.error('Failed to update customer:', err);
      alert(t('customer', 'editFailed'));
    }
  };

  const handleDeleteCustomer = async (customerId: string) => {
    try {
      await apiClient.deleteCustomer(customerId);
      setDeleteConfirm(null);
      loadCustomers();
    } catch (err) {
      console.error('Failed to delete customer:', err);
      alert(t('customer', 'deleteFailed'));
    }
  };

  const handleArchive = async (customerId: string) => {
    try {
      await apiClient.archiveCustomer(customerId);
      loadCustomers();
    } catch (err) {
      console.error('Failed to archive customer:', err);
      alert(t('customer', 'archiveFailed'));
    }
  };

  const handleRestore = async (customerId: string) => {
    try {
      await apiClient.restoreCustomer(customerId);
      loadCustomers();
    } catch (err) {
      console.error('Failed to restore customer:', err);
      alert(t('customer', 'restoreFailed'));
    }
  };

  const openLinkSurvey = async (customerId: string) => {
    setLinkingCustomer(customerId);
    setLoadingAvailable(true);
    try {
      const allSurveys = await apiClient.getSurveys(false);
      const customer = customers.find(c => c.customerId === customerId);
      const linkedIds = new Set(customer?.surveys?.map(s => s.surveyId) || []);
      setAvailableSurveys(allSurveys.filter(s => !linkedIds.has(s.surveyId)));
    } catch (err) {
      console.error('Failed to load surveys:', err);
    } finally {
      setLoadingAvailable(false);
    }
  };

  const handleLinkSurvey = async (surveyId: string) => {
    if (!linkingCustomer) return;
    try {
      await apiClient.linkSurveyToCustomer(linkingCustomer, surveyId);
      await loadCustomerSurveys(linkingCustomer);
      setAvailableSurveys(prev => prev.filter(s => s.surveyId !== surveyId));
    } catch (err) {
      console.error('Failed to link survey:', err);
      alert(t('customerManagement', 'linkFailed'));
    }
  };

  const handleUnlinkSurvey = async (customerId: string, surveyId: string) => {
    try {
      await apiClient.unlinkSurveyFromCustomer(customerId, surveyId);
      await loadCustomerSurveys(customerId);
    } catch (err) {
      console.error('Failed to unlink survey:', err);
      alert(t('customerManagement', 'unlinkFailed'));
    }
  };

  const btnStyle = (bg: string, color: string = 'white'): React.CSSProperties => ({
    padding: '6px 12px',
    backgroundColor: bg,
    color,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontWeight: 500
  });

  const statusLabel = (status: string) => {
    switch (status) {
      case 'draft': return { text: t('survey', 'statusDraft'), color: '#718096', bg: '#edf2f7' };
      case 'active': return { text: t('survey', 'statusActive'), color: '#2c7a7b', bg: '#e6fffa' };
      case 'closed': return { text: t('survey', 'statusClosed'), color: '#9b2c2c', bg: '#fff5f5' };
      default: return { text: status, color: '#718096', bg: '#edf2f7' };
    }
  };

  return (
    <div style={{ padding: '30px', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0 }}>{t('customerManagement', 'title')}</h2>
        <button
          onClick={() => setShowCreateForm(true)}
          style={{ padding: '10px 20px', backgroundColor: '#2c5282', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.95rem' }}
        >
          {t('customerManagement', 'createNew')}
        </button>
      </div>

      {/* Tab: Active / Archived */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
        <button
          onClick={() => setShowArchived(false)}
          style={{
            padding: '8px 16px',
            backgroundColor: !showArchived ? '#2c5282' : '#edf2f7',
            color: !showArchived ? 'white' : '#4a5568',
            border: 'none',
            borderRadius: '6px 6px 0 0',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: !showArchived ? 600 : 400
          }}
        >
          {t('customerManagement', 'active')}
        </button>
        <button
          onClick={() => setShowArchived(true)}
          style={{
            padding: '8px 16px',
            backgroundColor: showArchived ? '#2c5282' : '#edf2f7',
            color: showArchived ? 'white' : '#4a5568',
            border: 'none',
            borderRadius: '6px 6px 0 0',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: showArchived ? 600 : 400
          }}
        >
          {t('customerManagement', 'archived')}
        </button>
      </div>

      {loading ? (
        <p>{t('common', 'loading')}</p>
      ) : customers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ color: '#718096', fontSize: '1.1rem' }}>{t('customerManagement', 'noCustomers')}</p>
          {!showArchived && <p style={{ color: '#a0aec0' }}>{t('customerManagement', 'createPrompt')}</p>}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {customers.map(customer => {
            const isExpanded = expandedCustomers.has(customer.customerId);
            return (
              <div
                key={customer.customerId}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  overflow: 'hidden'
                }}
              >
                {/* Customer Header */}
                <div
                  style={{ padding: '16px 20px', cursor: 'pointer', borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none' }}
                  onClick={() => toggleExpand(customer.customerId)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '1.2rem', color: '#4a5568' }}>
                          {isExpanded ? '▼' : '▶'}
                        </span>
                        <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{customer.customerName}</h3>
                        {customer.projectName && (
                          <span style={{ fontSize: '0.85rem', color: '#718096', backgroundColor: '#edf2f7', padding: '2px 8px', borderRadius: '4px' }}>
                            {customer.projectName}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '16px', fontSize: '0.85rem', color: '#718096', marginTop: '6px', marginLeft: '24px' }}>
                        {customer.email && <span>{customer.email}</span>}
                        <span>{t('common', 'createdDate')} {new Date(customer.createdAt).toLocaleDateString('ja-JP')}</span>
                        {customer.surveys && <span>{t('customerManagement', 'linkedSurveys')}: {customer.surveys.length}</span>}
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: '6px', marginTop: '12px', marginLeft: '24px' }} onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => {
                        setEditingCustomer(customer.customerId);
                        setEditName(customer.customerName);
                        setEditProjectName(customer.projectName || '');
                        setEditEmail(customer.email || '');
                      }}
                      style={btnStyle('#edf2f7', '#2d3748')}
                    >
                      {t('common', 'edit')}
                    </button>
                    <button
                      onClick={() => openLinkSurvey(customer.customerId)}
                      style={btnStyle('#e6fffa', '#2c7a7b')}
                    >
                      {t('customerManagement', 'addSurvey')}
                    </button>
                    {!showArchived ? (
                      <button
                        onClick={() => handleArchive(customer.customerId)}
                        style={btnStyle('#ebf8ff', '#2c5282')}
                      >
                        {t('customer', 'archive')}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleRestore(customer.customerId)}
                        style={btnStyle('#ebf8ff', '#2c5282')}
                      >
                        {t('customer', 'restore')}
                      </button>
                    )}
                    <button
                      onClick={() => setDeleteConfirm(customer.customerId)}
                      style={btnStyle('#fff5f5', '#c53030')}
                    >
                      {t('common', 'delete')}
                    </button>
                  </div>
                </div>

                {/* Surveys List (Expanded) */}
                {isExpanded && (
                  <div style={{ padding: '16px 20px', backgroundColor: '#f7fafc' }}>
                    {customer.loadingSurveys ? (
                      <p style={{ color: '#718096', fontSize: '0.9rem' }}>{t('common', 'loading')}</p>
                    ) : !customer.surveys || customer.surveys.length === 0 ? (
                      <p style={{ color: '#a0aec0', fontSize: '0.9rem', textAlign: 'center', padding: '20px' }}>
                        {t('customerManagement', 'noLinkedSurveys')}
                      </p>
                    ) : (
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {customer.surveys.map(survey => {
                          const status = statusLabel(survey.status);
                          return (
                            <div
                              key={survey.surveyId}
                              style={{
                                backgroundColor: 'white',
                                borderRadius: '6px',
                                padding: '12px 16px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                border: '1px solid #e2e8f0'
                              }}
                            >
                              <div>
                                <div style={{ fontWeight: 500, fontSize: '0.95rem' }}>{survey.title}</div>
                                <div style={{ fontSize: '0.8rem', color: '#718096', marginTop: '4px' }}>
                                  {t('survey', 'responses')} {survey.completedCount}/{survey.respondentCount}{t('survey', 'respondentCount')}
                                  {' • '}
                                  {t('survey', 'deadline')} {new Date(survey.expiresAt).toLocaleDateString('ja-JP')}
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{
                                  padding: '4px 10px',
                                  backgroundColor: status.bg,
                                  color: status.color,
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: 600
                                }}>
                                  {status.text}
                                </span>
                                <button
                                  onClick={() => handleUnlinkSurvey(customer.customerId, survey.surveyId)}
                                  style={{ ...btnStyle('#fff5f5', '#c53030'), padding: '4px 8px', fontSize: '0.75rem' }}
                                >
                                  {t('customerManagement', 'unlink')}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Customer Dialog */}
      {showCreateForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '450px', width: '90%' }}>
            <h3 style={{ margin: '0 0 20px' }}>{t('customerManagement', 'createNew')}</h3>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 600 }}>
                {t('customerManagement', 'customerName')} *
              </label>
              <input
                type="text"
                value={newCustomerName}
                onChange={(e) => setNewCustomerName(e.target.value)}
                placeholder={t('customerManagement', 'customerNamePlaceholder')}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 600 }}>
                {t('customerManagement', 'projectName')}
              </label>
              <input
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder={t('customerManagement', 'projectNamePlaceholder')}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 600 }}>
                {t('common', 'email')}
              </label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={t('customerManagement', 'emailPlaceholder')}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCreateForm(false)} style={btnStyle('#edf2f7', '#4a5568')} disabled={creating}>
                {t('common', 'cancel')}
              </button>
              <button onClick={handleCreateCustomer} style={btnStyle('#2c5282')} disabled={creating || !newCustomerName.trim()}>
                {creating ? t('common', 'creating') : t('common', 'create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Customer Dialog */}
      {editingCustomer && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '450px', width: '90%' }}>
            <h3 style={{ margin: '0 0 20px' }}>{t('customer', 'editCustomer')}</h3>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 600 }}>
                {t('customerManagement', 'customerName')} *
              </label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 600 }}>
                {t('customerManagement', 'projectName')}
              </label>
              <input
                type="text"
                value={editProjectName}
                onChange={(e) => setEditProjectName(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 600 }}>
                {t('common', 'email')}
              </label>
              <input
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setEditingCustomer(null)} style={btnStyle('#edf2f7', '#4a5568')}>
                {t('common', 'cancel')}
              </button>
              <button onClick={() => handleEditCustomer(editingCustomer)} style={btnStyle('#2c5282')} disabled={!editName.trim()}>
                {t('common', 'save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Link Survey Dialog */}
      {linkingCustomer && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '550px', width: '90%', maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ margin: '0 0 16px' }}>{t('customerManagement', 'selectSurvey')}</h3>
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px' }}>
              {loadingAvailable ? (
                <p style={{ color: '#718096' }}>{t('common', 'loading')}</p>
              ) : availableSurveys.length === 0 ? (
                <p style={{ color: '#a0aec0', textAlign: 'center', padding: '20px' }}>
                  {t('customerManagement', 'noAvailableSurveys')}
                </p>
              ) : (
                <div style={{ display: 'grid', gap: '8px' }}>
                  {availableSurveys.map(survey => {
                    const status = statusLabel(survey.status);
                    return (
                      <div
                        key={survey.surveyId}
                        style={{
                          padding: '12px 16px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f7fafc'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
                        onClick={() => handleLinkSurvey(survey.surveyId)}
                      >
                        <div>
                          <div style={{ fontWeight: 500, fontSize: '0.95rem' }}>{survey.title}</div>
                          <div style={{ fontSize: '0.8rem', color: '#718096', marginTop: '4px' }}>
                            {t('survey', 'responses')} {survey.completedCount}/{survey.respondentCount}{t('survey', 'respondentCount')}
                            {' • '}
                            {t('survey', 'created')} {new Date(survey.createdAt).toLocaleDateString('ja-JP')}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            padding: '4px 10px',
                            backgroundColor: status.bg,
                            color: status.color,
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            {status.text}
                          </span>
                          <button style={{ ...btnStyle('#e6fffa', '#2c7a7b'), padding: '4px 10px' }}>
                            {t('common', 'add')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <button onClick={() => setLinkingCustomer(null)} style={btnStyle('#edf2f7', '#4a5568')}>
                {t('common', 'close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '400px', width: '90%' }}>
            <h3 style={{ margin: '0 0 16px' }}>{t('customerManagement', 'deleteConfirmTitle')}</h3>
            <p style={{ color: '#718096', marginBottom: '20px' }}>{t('customerManagement', 'deleteConfirmMessage')}</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteConfirm(null)} style={btnStyle('#edf2f7', '#4a5568')}>
                {t('common', 'cancel')}
              </button>
              <button onClick={() => handleDeleteCustomer(deleteConfirm)} style={btnStyle('#e53e3e')}>
                {t('common', 'delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagement;
